"use client";

import React from "react";
import "./DialogueSkeleton.css";

type CommonProps = {
  className?: string;
  "aria-label"?: string;
  dense?: boolean;            // ارتفاع جمع‌وجورتر
};

type ItemProps = CommonProps & {
  variant?: "item";           // پیش‌فرض item برای سازگاری
  isGroup?: boolean;
  hasUnread?: boolean;
  withIcons?: boolean;        // آیکن‌های سمت راست (کاربر/گروه)
  showMenu?: boolean;         // سه‌نقطه سمت راست
  showSensitive?: boolean;    // برچسب/قفل باریک
  titleWidthPct?: number;     // 30..70 برای طبیعی‌سازی
  subtitleWidthPct?: number;  // 20..60
};

type ListProps = CommonProps & {
  variant: "list";
  items?: number;             // تعداد آیتم‌ها (پیش‌فرض 4)
  // الگوی کلی لیست
  shape?: "user" | "group" | "mixed";
  withIcons?: boolean | "mixed";
  withMenu?: boolean | "mixed";
  withUnread?: boolean | "mixed";
  withSensitive?: boolean | "mixed";
};

type Props = ItemProps | ListProps;

// کمک: تولید مقدار pseudo-random پایدار برای تنوع عرض‌ها
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const pickPct = (seed: number, min: number, max: number) => {
  const r = Math.abs(Math.sin(seed)) % 1;
  return clamp(Math.round(min + r * (max - min)), min, max);
};

const DialogueSkeleton: React.FC<Props> = (props) => {
  if (props.variant === "list") {
    const {
      items = 4,
      shape = "mixed",
      withIcons = "mixed",
      withMenu = "mixed",
      withUnread = "mixed",
      withSensitive = "mixed",
      dense,
      className,
    } = props;

    const rows = Array.from({ length: items }).map((_, i) => {
      const isGroup =
        shape === "group" ? true : shape === "user" ? false : i % 2 === 1;

      const _withIcons =
        withIcons === "mixed" ? i % 2 === 0 : Boolean(withIcons);
      const _withMenu =
        withMenu === "mixed" ? i % 3 !== 1 : Boolean(withMenu);
      const _withUnread =
        withUnread === "mixed" ? i % 3 === 0 : Boolean(withUnread);
      const _withSensitive =
        withSensitive === "mixed" ? i % 4 === 0 : Boolean(withSensitive);

      const titleWidthPct = pickPct(i * 1.31 + 7, 38, 68);
      const subtitleWidthPct = pickPct(i * 2.19 + 3, 22, 46);

      return (
        <DialogueSkeleton
          key={i}
          variant="item"
          isGroup={isGroup}
          hasUnread={_withUnread}
          withIcons={_withIcons}
          showMenu={_withMenu}
          showSensitive={_withSensitive}
          dense={dense}
          titleWidthPct={titleWidthPct}
          subtitleWidthPct={subtitleWidthPct}
          aria-label="Loading conversation item"
        />
      );
    });

    return (
      <div
        className={`dlg-skel-list ${className || ""}`}
        role="status"
        aria-busy="true"
        aria-live="polite"
      >
        {rows}
      </div>
    );
  }

  // variant === "item"
  const {
    isGroup = false,
    hasUnread = false,
    withIcons = true,
    showMenu = true,
    showSensitive = false,
    dense = false,
    className,
    titleWidthPct = 50,
    subtitleWidthPct = 34,
  } = props as ItemProps;

  return (
    <div
      className={`dlg-skel ${dense ? "dense" : ""} ${className || ""}`}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      {/* Avatar */}
      <div className={`dlg-skel__avatar shimmer ${isGroup ? "group" : "user"}`} />

      {/* Texts */}
      <div className="dlg-skel__info">
        <div className="dlg-skel__title-row">
          <div
            className="dlg-skel__title shimmer"
            style={{ width: `${titleWidthPct}%` }}
          />
          {showSensitive && <div className="dlg-skel__pill shimmer" />}
        </div>
        <div
          className="dlg-skel__subtitle shimmer"
          style={{ width: `${subtitleWidthPct}%` }}
        />
      </div>

      {/* Right side */}
      <div className="dlg-skel__right">
        {hasUnread && <div className="dlg-skel__unread shimmer" />}
        {withIcons && (
          <>
            <div className="dlg-skel__icon shimmer" />
            {isGroup && <div className="dlg-skel__icon shimmer" />}
          </>
        )}
        {showMenu && <div className="dlg-skel__dots shimmer" />}
      </div>
    </div>
  );
};

export default DialogueSkeleton;
